import {
  dateLong,
  eurK,
  eurKSigned,
  fullEur,
  fullEurSigned,
  type OpsAchats,
  type OpsChantier,
  type OpsOrder,
  type OpsOrderLine,
} from '@cockpit/shared';
import { Badge, ErrorState, Spinner, Table, Td, Th, cn } from '@cockpit/ui';
import { Fragment, useState } from 'react';
import { plural } from '../../lib/format.js';
import { useOpsAchats, useOrderLines } from '../../lib/queries.js';
import { Section } from './parts.js';

const eur2 = (v: number) =>
  `${v < 0 ? '−' : ''}${new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Math.abs(v))} €`;
const qty = (v: number) => new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 2 }).format(v);
const ecartTone = (v: number | null) =>
  v == null || Math.abs(v) < 1 ? '' : v > 0 ? 'text-danger-700' : 'text-success-700';

/** Commandes d'achat rapprochées des factures fournisseurs, puis factures clients. */
export function FicheAchats({ r }: { r: OpsChantier }) {
  const q = useOpsAchats(r.code, true);
  return (
    <div className="flex flex-col gap-4">
      <Section title="Commandes d’achat" aside="cliquez une commande pour le détail ligne à ligne">
        {q.isLoading ? (
          <Spinner label="Lecture des commandes dans Flex…" />
        ) : q.isError ? (
          <ErrorState>{(q.error as Error).message}</ErrorState>
        ) : (
          <Orders r={r} orders={q.data!.orders} />
        )}
      </Section>
      <Section title="Factures clients">
        {q.isLoading ? (
          <Spinner label="Lecture des factures dans Flex…" />
        ) : q.isError ? null : (
          <Invoices list={q.data!.invoices} />
        )}
      </Section>
    </div>
  );
}

function Orders({ r, orders }: { r: OpsChantier; orders: OpsAchats['orders'] }) {
  const [open, setOpen] = useState<string | null>(null);
  if (!orders.length)
    return (
      <p className="text-[13.5px] text-ink-3">
        Aucune commande d’achat ni facture fournisseur sur ce chantier à cette date.
      </p>
    );
  const tot = orders.reduce(
    (a, o) => ({
      m: a.m + (o.m ?? 0),
      fv: a.fv + o.fv,
      u: a.u + (o.u ?? 0),
      e: a.e + (o.ecart != null && Math.abs(o.ecart) >= 1 ? o.ecart : 0),
    }),
    { m: 0, fv: 0, u: 0, e: 0 },
  );
  const n = orders.filter((o) => !o.orphan).length;
  return (
    <>
      <ul className="flex flex-wrap gap-2 text-[13px]">
        {[
          [plural(n, 'commande'), eurK(tot.m)],
          ['Facturé fournisseurs', eurK(tot.fv)],
          ['Reste à facturer', eurK(tot.u)],
          ['Écarts facture / commande', eurKSigned(tot.e)],
        ].map(([l, v]) => (
          <li key={l} className="rounded-sm bg-surface-2 px-2.5 py-1 text-ink-2">
            {l} <b className="tnum ml-1 text-ink">{v}</b>
          </li>
        ))}
      </ul>
      <Table minWidth={880}>
        <thead>
          <tr>
            <Th>Commande</Th>
            <Th>Fournisseur · objet</Th>
            <Th>Type</Th>
            <Th>Statut</Th>
            <Th align="right">Commandé</Th>
            <Th align="right">Facturé</Th>
            <Th align="right">Reste à facturer</Th>
            <Th align="right">Écart facture</Th>
          </tr>
        </thead>
        <tbody>
          {orders.map((o) => {
            const key = o.o || `orphan-${o.f}`;
            const expanded = open === key;
            return (
              <Fragment key={key}>
                <tr
                  tabIndex={o.o ? 0 : -1}
                  aria-expanded={o.o ? expanded : undefined}
                  onClick={() => o.o && setOpen(expanded ? null : key)}
                  onKeyDown={(e) => {
                    if (o.o && (e.key === 'Enter' || e.key === ' ')) {
                      e.preventDefault();
                      setOpen(expanded ? null : key);
                    }
                  }}
                  className={cn('align-top', o.o && 'cursor-pointer hover:bg-surface-2')}
                >
                  <Td className="tnum">
                    {expanded ? '▾ ' : o.o ? '▸ ' : ''}
                    {o.o || 'Sans commande'}
                    <small className="block text-[12px] text-ink-3">
                      {o.d ? dateLong(o.d) : ''}
                      {o.ach ? ` · ${o.ach}` : ''}
                    </small>
                  </Td>
                  <Td>
                    {o.f ?? '—'}
                    {o.ds && <small className="block text-[12px] text-ink-3">{o.ds}</small>}
                    {o.orphan && (
                      <small className="block text-[12px] text-ink-3">
                        factures fournisseurs rattachées au chantier
                      </small>
                    )}
                  </Td>
                  <Td>
                    <Badge tone={o.isST ? 'azur' : 'neutral'}>{o.isST ? 'ST' : 'Achat'}</Badge>
                  </Td>
                  <Td>{o.orphan ? '—' : o.st}</Td>
                  <Td align="right">{o.m != null ? fullEur(o.m) : '—'}</Td>
                  <Td align="right">
                    {fullEur(o.fv)}
                    <small className="block text-[12px] text-ink-3">{plural(o.nf, 'facture')}</small>
                  </Td>
                  <Td align="right">{o.u != null ? fullEur(o.u) : '—'}</Td>
                  <Td align="right" className={ecartTone(o.ecart)}>
                    {o.ecart == null ? '—' : Math.abs(o.ecart) < 1 ? 'conforme' : fullEurSigned(o.ecart)}
                  </Td>
                </tr>
                {expanded && (
                  <tr>
                    <td colSpan={8} className="bg-surface-2 px-3 py-3">
                      <Lines code={r.code} o={o} />
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </Table>
      <p className="text-[12.5px] text-ink-3">
        Écart facture = facturé + reste à facturer − commandé : positif, le fournisseur facture plus que la
        commande. Il n’est calculé qu’à la date de la dernière synchronisation.
      </p>
    </>
  );
}

function Lines({ code, o }: { code: string; o: OpsOrder }) {
  const q = useOrderLines(code, o.o, true);
  if (q.isLoading) return <Spinner label="Lecture des lignes…" />;
  if (q.isError) return <ErrorState>{(q.error as Error).message}</ErrorState>;
  const L: OpsOrderLine[] = q.data ?? [];
  if (!L.length)
    return <p className="text-[13px] text-ink-3">Aucune ligne de commande ni de facture à cette date.</p>;
  return (
    <>
      <Table minWidth={760}>
        <thead>
          <tr>
            <Th>Ligne</Th>
            <Th>Article</Th>
            <Th align="right">Qté cdée</Th>
            <Th align="right">PU commande</Th>
            <Th align="right">Montant cdé</Th>
            <Th align="right">Qté facturée</Th>
            <Th align="right">PU facturé</Th>
            <Th align="right">Montant facturé</Th>
            <Th align="right">Écart</Th>
          </tr>
        </thead>
        <tbody>
          {L.map((l, i) => (
            <tr key={`${l.l}-${i}`} className="align-top">
              <Td className="tnum">{l.l}</Td>
              <Td>
                {l.ds}
                <small className="block text-[12px] text-ink-3">
                  {l.extra
                    ? `facture ${l.extra.n} · ${dateLong(l.extra.d)} · compte ${l.acc ?? '—'}`
                    : `${l.art ?? ''} · compte ${l.acc ?? '—'}`}
                </small>
              </Td>
              <Td align="right">{l.extra ? '' : qty(l.q)}</Td>
              <Td align="right">{l.extra ? '' : eur2(l.pu)}</Td>
              <Td align="right">{l.extra ? '' : fullEur(l.m)}</Td>
              <Td align="right">{l.qf != null ? qty(l.qf) : '—'}</Td>
              <Td align="right">{l.puf != null ? eur2(l.puf) : '—'}</Td>
              <Td align="right">{l.vf != null ? fullEur(l.vf) : '—'}</Td>
              <Td align="right" className={ecartTone(l.ecart)}>
                {l.ecart == null
                  ? 'non facturé'
                  : Math.abs(l.ecart) < 1
                    ? 'conforme'
                    : fullEurSigned(l.ecart)}
              </Td>
            </tr>
          ))}
        </tbody>
      </Table>
      <p className="mt-2 text-[12.5px] text-ink-3">
        Écart par ligne = montant facturé − montant commandé pour la quantité facturée. Les factures listent
        tous les comptes de la commande (éco-participation, transport…).
      </p>
    </>
  );
}

function Invoices({ list }: { list: OpsAchats['invoices'] }) {
  if (!list.length) return <p className="text-[13.5px] text-ink-3">Aucune facture client à cette date.</p>;
  return (
    <Table minWidth={560}>
      <thead>
        <tr>
          <Th>Facture</Th>
          <Th>Date</Th>
          <Th>Objet</Th>
          <Th align="right">Montant HT</Th>
        </tr>
      </thead>
      <tbody>
        {list.map((x) => (
          <tr key={x.n + x.d}>
            <Td className="tnum">
              {x.n}
              {x.t && <small className="block text-[12px] text-ink-3">{x.t}</small>}
            </Td>
            <Td>{dateLong(x.d)}</Td>
            <Td>{x.ds}</Td>
            <Td align="right" className={cn(x.v < 0 && 'text-danger-700')}>
              {fullEur(x.v)}
            </Td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
}
